import React, { PropsWithChildren, useRef, useEffect } from 'react';
import MonthItem, { MonthItemProps } from './MonthItem';
import { getMonthNumber } from './helper';
import { DayData, MonthHandler } from '../types';
import { FnOnDayClick, FnOnDayHover } from './hooks';
import Box from '../../../elements/Box';

export type CalendarContentProps = Pick<
  MonthItemProps,
  'weekdayItem' | 'dayItems' | 'captionItem'
> & {
  formatMonth?: string;
  numberOfMonths?: number;
  renderDay?: any;
  onDayClick?: FnOnDayClick;
  onDayHover?: FnOnDayHover;
  displayDate: Date;
  months: Array<MonthHandler>;
  onScroll?: (e: any) => void;
  isMobile?: boolean;
  showYear?: boolean;
  showWeekDayNames?: boolean;
  weekDaysContainer?: any;
  calendarContainer?: any;
  calendarMonthContainer?: any;
  dispatch?: any;
  currentDate?: any;
  preSelectedDate?: Date;
  setWasOpenPopup?: (value: boolean) => void;
  setUserInteraction?: (value: boolean) => void;
  isDateRange?: boolean;
  componentName?: string;
  dayWidthSetting?: {
    enableDynamicWidthCell?: boolean;
    width?: number;
  };
  isMobileView?: boolean;
  calendarMonthsContainerStyle?: React.CSSProperties;
};

const CalendarContent = React.forwardRef<HTMLDivElement, CalendarContentProps>(
  (
    {
      captionItem,
      dayItems,
      weekdayItem,
      formatMonth = 'MMMM YYYY',
      numberOfMonths = 2,
      renderDay,
      onScroll = () => {},
      onDayClick = () => {},
      onDayHover = () => {},
      displayDate = new Date(),
      months,
      children,
      isMobile = false,
      isDateRange = false,
      showYear,
      showWeekDayNames = true,
      weekDaysContainer,
      calendarContainer,
      calendarMonthContainer,
      setWasOpenPopup,
      currentDate,
      dispatch,
      preSelectedDate,
      setUserInteraction,
      componentName = 'SingleDatePicker',
      dayWidthSetting = {
        enableDynamicWidthCell: false,
        width: 39
      },
      isMobileView,
      calendarMonthsContainerStyle
    }: PropsWithChildren<CalendarContentProps>,
    ref
  ) => {
    const monthRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
      if (isMobile) {
        setTimeout(() => {
          if (monthRef && monthRef.current) {
            monthRef.current.scrollIntoView({ block: 'center', behavior: 'smooth' });
          }
        }, 250);
      } else {
        if (currentDate?.start) {
          dispatch({ type: 'SET_DISPLAY_DATE_ACTION', payload: currentDate?.start });
        } else {
          dispatch({ type: 'SET_DISPLAY_DATE_ACTION', payload: new Date() });
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const newStyle =
      isMobileView && calendarMonthsContainerStyle ? calendarMonthsContainerStyle : {};
    const handleDayClick = (dayData: DayData) => {
      onDayClick(dayData);
    };
    return (
      <Box
        className={isMobile ? 'calendar-content-container-mobile' : 'calendar-content-container'}
        ref={ref}
        dataTestId="calendar-months-container"
        style={newStyle}
        {...calendarContainer}
      >
        {months.map((month, index) => {
          const isSelectedMonth =
            currentDate !== null &&
            currentDate !== undefined &&
            currentDate?.start !== '' &&
            month?.getMonth()?.getMonth() === currentDate?.start?.getMonth() &&
            month?.getMonth()?.getFullYear() === currentDate?.start?.getFullYear();

          return (
            <MonthItem
              ref={isSelectedMonth ? monthRef : undefined}
              captionItem={captionItem}
              weekdayItem={weekdayItem}
              dayItems={dayItems}
              key={`month-${getMonthNumber(displayDate) + index}-${index}`}
              month={month}
              renderDay={renderDay}
              onDayClick={handleDayClick}
              onDayHover={onDayHover}
              showYear={showYear}
              showWeekDayNames={showWeekDayNames}
              weekDaysContainer={weekDaysContainer}
              calendarMonthContainer={calendarMonthContainer}
              setWasOpenPopup={setWasOpenPopup}
              isMobile={isMobile}
              isDateRange={isDateRange}
              preSelectedDate={preSelectedDate}
              setUserInteraction={setUserInteraction}
              componentName={componentName}
              dayWidthSetting={dayWidthSetting}
            />
          );
        })}
        {children}
      </Box>
    );
  }
);

export default CalendarContent;
